export const CUSTOMER_REGISTRY_TYPES = Object.freeze([
  { code: "PRIVATO", description: "Privato", order: 10, entityType: "PERSON", personDataRequired: true, businessNameRequired: false, fiscalCodeRequiredInItaly: true, vatNumberRequired: false, sdiCodeRequiredInItaly: false, sdiLength: 7 },
  { code: "DITTA_INDIVIDUALE", description: "Ditta Individuale", order: 20, entityType: "PERSON_BUSINESS", personDataRequired: true, businessNameRequired: true, fiscalCodeRequiredInItaly: true, vatNumberRequired: true, sdiCodeRequiredInItaly: true, sdiLength: 7 },
  { code: "DITTA_SOCIETA", description: "Ditta / Società", order: 40, entityType: "COMPANY", personDataRequired: false, businessNameRequired: true, fiscalCodeRequiredInItaly: true, vatNumberRequired: true, sdiCodeRequiredInItaly: true, sdiLength: 7 },
  { code: "PUBBLICA_AMMINISTRAZIONE", description: "Pubblica Amministrazione", order: 70, entityType: "PUBLIC_ADMIN", personDataRequired: false, businessNameRequired: true, fiscalCodeRequiredInItaly: true, vatNumberRequired: true, sdiCodeRequiredInItaly: true, sdiLength: 6 },
]);

const MONTH_CODES = ["A", "B", "C", "D", "E", "H", "L", "M", "P", "R", "S", "T"];
const ODD_MAP = {
  0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
  K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
  U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};
const OMOCODIA_TO_DIGIT = Object.freeze({ L: "0", M: "1", N: "2", P: "3", Q: "4", R: "5", S: "6", T: "7", U: "8", V: "9" });
const OMOCODIA_POSITIONS = new Set([6, 7, 9, 10, 12, 13, 14]);

function clean(value) { return String(value || "").trim(); }
function token(value) {
  return clean(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}
function letters(value) {
  return clean(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^A-Za-z]/g, "").toUpperCase();
}
function consonants(value) { return letters(value).replace(/[AEIOU]/g, ""); }
function vowels(value) { return letters(value).replace(/[^AEIOU]/g, ""); }
function pad3(value) { return `${value}XXX`.slice(0, 3); }

export function getCustomerRegistryType(value) {
  const normalized = token(value);
  if (!normalized) return null;
  return CUSTOMER_REGISTRY_TYPES.find((item) => token(item.code) === normalized || token(item.description) === normalized) || null;
}

export function getCustomerRegistryTypeFromCustomer(customer = {}, catalog = []) {
  const id = clean(customer.customerTypeId || customer.tipoAnagraficaId);
  const label = clean(customer.customerType || customer.tipoAnagrafica || customer.tipoCliente);
  const item = (catalog || []).find((row) => String(row?._id || row?.code || "") === id || clean(row?.description).toLocaleLowerCase("it") === label.toLocaleLowerCase("it"));
  return getCustomerRegistryType(item?.code || item?._id || item?.description || id || label);
}

export function isPersonRegistryType(value) {
  return Boolean(getCustomerRegistryType(value)?.personDataRequired);
}

export function normalizeCustomerGender(value) {
  const normalized = token(value);
  if (["M", "MASCHIO", "MALE", "UOMO"].includes(normalized)) return "M";
  if (["F", "FEMMINA", "FEMALE", "DONNA"].includes(normalized)) return "F";
  return "";
}

export function canCalculateCustomerFiscalCode(customer = {}, catalog = []) {
  return Boolean(getCustomerRegistryTypeFromCustomer(customer, catalog)?.personDataRequired);
}

export function requiredCustomerFields(typeValue, country = "Italia") {
  const type = getCustomerRegistryType(typeValue);
  const italy = ["italia", "italy", "it"].includes(clean(country).toLocaleLowerCase("it"));
  const required = new Set(["customerType", "country"]);
  if (!type) return required;
  if (type.businessNameRequired) required.add("businessName");
  if (type.personDataRequired) ["name", "surname", "gender", "birthDate", "birthCountry", "birthCity", "nationality"].forEach((field) => required.add(field));
  if (type.fiscalCodeRequiredInItaly && italy) required.add("fiscalCode");
  if (!italy || type.vatNumberRequired) required.add("vatNumber");
  if (type.sdiCodeRequiredInItaly && italy) required.add("sdiCode");
  return required;
}

function surnameCode(value) {
  return pad3(`${consonants(value)}${vowels(value)}`);
}
function nameCode(value) {
  const cons = consonants(value);
  if (cons.length >= 4) return `${cons[0]}${cons[2]}${cons[3]}`;
  return pad3(`${cons}${vowels(value)}`);
}
function controlCharacter(first15) {
  const code = String(first15 || "").toUpperCase();
  if (!/^[A-Z0-9]{15}$/.test(code)) return "";
  let sum = 0;
  for (let index = 0; index < 15; index += 1) {
    const char = code[index];
    sum += index % 2 === 0 ? ODD_MAP[char] : (/\d/.test(char) ? Number(char) : char.charCodeAt(0) - 65);
  }
  return String.fromCharCode(65 + (sum % 26));
}
function parseBirthDate(value) {
  const raw = clean(value);
  if (!raw) return null;
  let day; let month; let year;
  let match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) { day = Number(match[1]); month = Number(match[2]); year = Number(match[3]); }
  else {
    match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    year = Number(match[1]); month = Number(match[2]); day = Number(match[3]);
  }
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function calculateItalianFiscalCode({ name, surname, gender, birthDate, cadastralCode } = {}) {
  const date = parseBirthDate(birthDate);
  const cadastral = token(cadastralCode);
  const sex = token(gender);
  if (!letters(name) || !letters(surname) || !date || !["M", "F"].includes(sex) || !/^[A-Z][0-9]{3}$/.test(cadastral)) return "";
  const year = String(date.getFullYear()).slice(-2);
  const month = MONTH_CODES[date.getMonth()];
  const day = String(date.getDate() + (sex === "F" ? 40 : 0)).padStart(2, "0");
  const first15 = `${surnameCode(surname)}${nameCode(name)}${year}${month}${day}${cadastral}`;
  return `${first15}${controlCharacter(first15)}`;
}

export function validItalianFiscalCode(value) {
  const code = token(value);
  return /^[A-Z0-9]{16}$/.test(code) && controlCharacter(code.slice(0, 15)) === code[15];
}

function canonicalizeOmocodia(value) {
  return String(value || "").toUpperCase().split("").map((char, index) => OMOCODIA_POSITIONS.has(index) && OMOCODIA_TO_DIGIT[char] ? OMOCODIA_TO_DIGIT[char] : char).join("");
}

export function fiscalCodeMatchesPersonalData(value, personalData = {}) {
  const fiscalCode = token(value);
  if (!validItalianFiscalCode(fiscalCode)) return false;
  const expected = calculateItalianFiscalCode(personalData);
  if (!expected) return true;
  return canonicalizeOmocodia(fiscalCode.slice(0, 15)) === canonicalizeOmocodia(expected.slice(0, 15));
}
